// src/hooks/useAICharactersConfig.js

import { useState, useCallback } from 'react';
import { getConfig, saveConfig } from '../utils/configUtils';
import { defaultCharacterConfig, defaultCharactersConfig } from '../utils/defaultConfig';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser'

const useAICharactersConfig = (pca) => {
  const [aiCharactersConfig, setAiCharactersConfig] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const { instance, accounts } = useMsal();
  const userId = accounts[0]?.idTokenClaims?.sub;

  // Memoize the replaceAICharacterConfig function
  const replaceAICharacterConfig = useCallback((newConfig) => {
    setAiCharactersConfig(newConfig);
    //console.info('Character config replaced.');
  }, [setAiCharactersConfig]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      instance.acquireTokenSilent({
        scopes: ["https://aiconsultantllc.onmicrosoft.com/4f2dc587-5caa-47a1-860d-6d4049611d78/get_config"],
        account: accounts[0],
      }).then(async (response) => {
        const jsonConfig = await getConfig(userId, response.accessToken);
        if (Array.isArray(jsonConfig)) {
          replaceAICharacterConfig(jsonConfig);  // Directly replace with the loaded config
        } else {
          //console.error('Loaded config is not an array:', jsonConfig);
        }
        setLoading(false);
      }).catch(async (error) => {
        // Handle interaction required error
        if (error instanceof InteractionRequiredAuthError) {
          // Fallback to interactive login (redirect)
          instance.acquireTokenRedirect({
            scopes: ["https://aiconsultantllc.onmicrosoft.com/4f2dc587-5caa-47a1-860d-6d4049611d78/get_config"],
            prompt: "login", // Force interactive login
          });
        } else {
          //console.error('Error loading AI Characters config:', error);
        }
        setLoading(false);
      });
    } catch (error) {
      //console.error('Error loading AI Characters config:', error);
      setLoading(false);
    }
  }, [instance, accounts, replaceAICharacterConfig, userId, setLoading]);


  const saveEditedCharacterConfig = (editingConfig, updatedConfig) => {
    if (aiCharactersConfig.some(config => config.name === editingConfig.name)) {
      updateAICharacterConfig(editingConfig.name, updatedConfig);
    } else {
      addNewAICharacterConfig(updatedConfig);
    }
  };

  const addNewAICharacterConfig = (newAICharacterConfig) => {
    const updatedConfigs = [...aiCharactersConfig, newAICharacterConfig];
    replaceAICharacterConfig(updatedConfigs);  // Replace the entire array
    instance.acquireTokenSilent({
      scopes: ["https://aiconsultantllc.onmicrosoft.com/4f2dc587-5caa-47a1-860d-6d4049611d78/save_config"],
      account: accounts[0],
    })
      .then((response) => {
        saveConfig(userId, response.accessToken, updatedConfigs);
      }).catch((error) => {
        //console.error('Error saving AI Characters config:', error);
      });
  };

  const updateAICharacterConfig = (name, updatedConfig) => {
    const updatedConfigs = aiCharactersConfig.map(config =>
      config.name === name ? updatedConfig : config
    );
    replaceAICharacterConfig(updatedConfigs);
    instance.acquireTokenSilent({
      scopes: ["https://aiconsultantllc.onmicrosoft.com/4f2dc587-5caa-47a1-860d-6d4049611d78/save_config"],
      account: accounts[0],
    })
      .then((response) => {
        saveConfig(userId, response.accessToken, updatedConfigs);
      }).catch((error) => {
        //console.error('Error saving AI Characters config:', error);
      });
  };

  const deleteCharacterConfig = (characterName) => {
    const updatedConfigs = aiCharactersConfig.filter(config => config.name !== characterName);
    replaceAICharacterConfig(updatedConfigs);
    instance.acquireTokenSilent({
      scopes: ["https://aiconsultantllc.onmicrosoft.com/4f2dc587-5caa-47a1-860d-6d4049611d78/save_config"],
      account: accounts[0],
    })
      .then((response) => {
        saveConfig(userId, response.accessToken, updatedConfigs);
      }).catch((error) => {
        //console.error('Error saving AI Characters config:', error);
      });
  };

  const getNewDefaultAICharacterConfig = () => {
    const tempName = getNewDefaultAICharacterName();
    return {
      ...defaultCharacterConfig,
      name: tempName
    };
  };

  const characterNameExists = (name) => {
    return aiCharactersConfig.some(config => config.name === name);
  };

  const handleResetToDefaults = () => {
    replaceAICharacterConfig(defaultCharactersConfig);
    instance.acquireTokenSilent({
      scopes: ["https://aiconsultantllc.onmicrosoft.com/4f2dc587-5caa-47a1-860d-6d4049611d78/save_config"],
      account: accounts[0],
    })
      .then((response) => {
        saveConfig(userId, response.accessToken, defaultCharactersConfig);
      }).catch((error) => {
        //console.error('Error saving AI Characters config:', error);
      });
  };

  const getNewDefaultAICharacterName = (fromName) => {
    let nameToSave = fromName || defaultCharacterConfig.name;
    const existingNames = aiCharactersConfig.map(config => config.name);

    if (existingNames.includes(nameToSave)) {
      let counter = 1;
      let newName = `${nameToSave} (${counter})`;

      while (existingNames.includes(newName)) {
        counter += 1;
        newName = `${nameToSave} (${counter})`;
      }

      nameToSave = newName; // Use the new unique name
    }


    return nameToSave;
  };

  const handleCheckCharacterConfigImport = (configArray) => {
    // Check if configArray is an array
    if (configArray && Array.isArray(configArray)) {
      // Loop through each config in the array
      let result = true;
      for (const config of configArray) {
        // Check if the required properties exist
        result = result && isConfig(config);
        if (!result) {
          break;
        }
      }
      return result;
    }
    return false;
  };
  const handleImportNewCharactersConfig = (configArray) => {
    if (handleCheckCharacterConfigImport(configArray)) {
      replaceAICharacterConfig(configArray);
      instance.acquireTokenSilent({
        scopes: ["https://aiconsultantllc.onmicrosoft.com/4f2dc587-5caa-47a1-860d-6d4049611d78/save_config"],
        account: accounts[0],
      })
        .then((response) => {
          saveConfig(userId, response.accessToken, configArray);
        }).catch((error) => {
          //console.error('Error saving AI Characters config:', error);
        });
      return true;
    }
    return false;
  };

  const isConfig = (config) => {
    return config &&
      typeof config === 'object' &&
      'name' in config &&
      'role' in config &&
      'systemMessage' in config &&
      'assistantMessage' in config &&
      'temperature' in config &&
      'max_tokens' in config &&
      'top_p' in config;
  }
  return [aiCharactersConfig,
    saveEditedCharacterConfig,
    deleteCharacterConfig,
    getNewDefaultAICharacterConfig,
    handleResetToDefaults,
    isConfig,
    handleImportNewCharactersConfig,
    characterNameExists,
    getNewDefaultAICharacterName,
    loadConfig,
    isLoading];
};

export default useAICharactersConfig;
